<template>
  <div class="referral-program-view">
    <HeadSection
      :breadcrumbs="breadcrumbs"
      :permissionOfSection="permissionOfSection"
    />
    <MainContainer :loading="userLoading" :permissionOfSection="permissionOfSection" :isBlockStyle="true">
      <div class="d-flex flex-column" style="gap: 20px;">
        <ContentBlock style="width: 100%;" v-if="program">
          <template #title>
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <FormTitle :title="innerProgramName" :indent="false" class="mr-3" />
                <a-badge
                  :count="programStatusName"
                  :number-style="{
                    backgroundColor: programStatusColors.background,
                    color: programStatusColors.color,
                  }"
                  style="transform: scale(1.2);"
                />
              </div>
              <div class="d-flex align-items-center" v-if="editPermission">
                <a-button
                  size="large"
                  :disabled="!editPermission"
                  class="statistic-button bg-orange text-white mr-3"
                  @click="activityLogProgram"
                >
                  <span class="px-2">Activity log</span>
                </a-button>
                <a-button
                  size="large"
                  :disabled="!editPermission"
                  :class="{'mr-3' : showStartStopButton}"
                  @click="detailsProgram"
                >
                  <span class="px-2">{{ detailsButtonText }}</span>
                </a-button>
                <a-button
                  v-if="showStartStopButton"
                  :type="isActiveProgram ? 'danger' : 'primary'"
                  size="large"
                  :disabled="!editPermission"
                  @click="toggleChangeStatusRefProgramModal(true)"
                >
                  <span class="px-2">{{ isActiveProgram ? 'Stop' : 'Start'}}</span>
                </a-button>
              </div>
            </div>
          </template>
          <template #content>
            <div class="flex flex-col w-full">
              <div v-if="program.description" class="mb-3">
                <span class="d-block text-default uppercase mr-1">DESCRIPTION</span>
                <span>{{ program.description }}</span>
              </div>
              <div class="general-info mb-5">
                <div class="d-flex flex-column" style="max-width: 500px;">
                  <div class="d-flex flex-column mb-2">
                    <div class="d-flex align-items-center mb-1">
                      <span class="d-block text-default uppercase mr-1">REFERRAL PROGRAM NAME</span>
                      <a-tooltip placement="topLeft" arrow-point-at-center>
                        <template slot="title">
                          <span>This text will be displayed as the period name in the app interface</span>
                        </template>
                        <a-icon class="d-flex ml-2" type="info-circle" />
                      </a-tooltip>
                    </div>
                    <span>{{ programName }}</span>
                  </div>
                  <div class="d-flex flex-column">
                    <div class="d-flex align-items-center mb-1">
                      <span class="d-block text-default uppercase mr-1">AVATAR CATEGORY</span>
                      <a-tooltip placement="topLeft" arrow-point-at-center>
                        <template slot="title">
                          <span>Affiliates will be able to choose avatars from this category</span>
                        </template>
                        <a-icon class="d-flex ml-2" type="info-circle" />
                      </a-tooltip>
                    </div>
                    <span>{{ program.affiliate_settings.avatar_category.name || '-' }}</span>
                  </div>
                </div>
                <div class="d-flex flex-column">
                  <span class="d-block text-default uppercase mb-1">ACTIVITY INFO</span>
                  <div class="d-flex flex-column" style="min-width: 300px; max-width: 500px;">
                    <a-progress :percent="programPercent" :show-info="false" />
                    <div class="d-flex align-items-center justify-content-between">
                      <div v-if="programStartedAt" class="d-flex flex-column">
                        <span class="font-size-15">{{ programStartedAt.date }}</span>
                        <span class="d-flex align-items-center font-size-14 text-default">
                          <a-icon type="clock-circle" class="mr-2" /> {{ programStartedAt.time }}
                        </span>
                      </div>
                      <div v-if="programExpiresAt" class="d-flex flex-column">
                        <span class="font-size-15">{{ programExpiresAt.date }}</span>
                        <span class="d-flex align-items-center justify-content-end font-size-14 text-default">
                          <a-icon type="clock-circle" class="mr-2" /> {{ programExpiresAt.time }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <FormTitle title="Role settings" />
              <div class="role-tabs">
                <a-tabs type="card" v-model="activeRoleTab">
                  <a-tab-pane
                    v-for="(role, roleIndex) in availableRoles"
                    :key="role.system_name"
                    force-render
                  >
                    <template #tab>
                      <DefaultLabelTab
                        :name="role.name"
                        :error_validate="availableRoles[roleIndex].error_validate"
                      />
                    </template>
                    <div class="referral-program-view__container">
                      <div class="referral-program-view__conditions mb-3">
                        <FormSubTitle title="Conditions" />
                        <Condition
                          v-for="(condition, index) in program[`${role.system_name}_settings`].conditions"
                          :key="index"
                          :ref="`${role.system_name}-conditions-${index}`"
                          :isOnlyView="true"
                          :editPermission="editPermission"
                          :languagesUsed="languagesUsed"
                          :condition="condition"
                          :conditionValue="getConditionValue(condition)"
                          @show="toggleShowConditionModal(true, role.system_name, index, condition)"
                        />
                      </div>
                      <div class="referral-program-view__cashback">
                        <FormSubTitle title="Cashback settings" />
                        <div class="d-flex" style="gap: 10px;">
                          <div class="referral-program-view__block">
                            <span class="text-default font-weight-bold">Percentage</span>
                            <span class="font-size-18 font-weight-bold">{{ program[`${role.system_name}_settings`].cashback_percent }}</span>
                          </div>
                          <div class="referral-program-view__block">
                            <span class="text-default font-weight-bold">Max Amount</span>
                            <span class="font-size-18 font-weight-bold">{{ program[`${role.system_name}_settings`].cashback_limit }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <FormSubTitle title="Additional" />
                    <div class="d-flex align-items-center">
                      <div
                        class="referral-program-view__block block-hover cursor-pointer flex-row align-items-center mr-2"
                        @click="toggleShowRulesetModal(true, program[`${role.system_name}_settings`].rule_set)"
                      >
                        <span>Ruleset</span>
                        <div class="d-flex align-items-center ml-1">
                          <a-icon type="right" class="font-size-12" />
                        </div>
                      </div>
                      <div
                        class="referral-program-view__block block-hover cursor-pointer flex-row align-items-center mr-2"
                        @click="toggleShowFaqsetModal(true, program[`${role.system_name}_settings`].faq_set)"
                      >
                        <span>FAQset</span>
                        <div class="d-flex align-items-center ml-1">
                          <a-icon type="right" class="font-size-12" />
                        </div>
                      </div>
                      <div
                        class="referral-program-view__block block-hover cursor-pointer flex-row align-items-center"
                        @click="toggleAddConditionModal(true, program[`${role.system_name}_settings`].additional_conditions)"
                      >
                        <span>Conditions description</span>
                        <div class="d-flex align-items-center ml-1">
                          <a-icon type="right" class="font-size-12" />
                        </div>
                      </div>
                    </div>
                  </a-tab-pane>
                </a-tabs>
              </div>
            </div>
          </template>
        </ContentBlock>
        <div class="d-flex flex-wrap" style="gap: 20px;">
          <MembersDiagram style="flex: 1 1 49%" />
          <CashbackDiagram style="flex: 1 1 49%" />
        </div>
      </div>
    </MainContainer>
    <ConditionModal
      v-if="showConditionModal"
      :show="showConditionModal"
      :languagesUsed="languagesUsed"
      :selectedCondition="targetCondition"
      :targetConditionValue="targetConditionValue"
      :isOnlyView="true"
      @close="toggleShowConditionModal"
    />
    <ViewRulesetModal
      v-if="showRulesetModal"
      :show="showRulesetModal"
      :languagesUsed="languagesUsed"
      :selectedRuleset="selectedRuleset"
      @close="toggleShowRulesetModal"
    />
    <ViewFaqsetModal
      v-if="showFaqsetModal"
      :show="showFaqsetModal"
      :languagesUsed="languagesUsed"
      :selectedFaqset="selectedFaqset"
      @close="toggleShowFaqsetModal"
    />
    <ViewAdditionalConditionModal
      v-if="showAddConditionModal"
      :show="showAddConditionModal"
      :languagesUsed="languagesUsed"
      :selectedConditions="selectedConditions"
      @close="toggleAddConditionModal"
    />
    <ChangeStatusReferralProgramModal
      v-if="showChangeStatusRefProgramModal"
      :show="showChangeStatusRefProgramModal"
      :program="program"
      :isActiveProgram="isActiveProgram"
      @updateStatus="updateRefProgramStatus"
      @close="toggleChangeStatusRefProgramModal"
    />
  </div>
</template>

<script>
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import ContentBlock from '@/components/main/contentBlock'
import FormTitle from '@/components/custom/form/FormTitle.vue'
import FormSubTitle from '@/components/custom/form/FormSubTitle.vue'
import DefaultLabelTab from '@/views/apps/referral-program/components/DefaultLabelTab.vue'
import Condition from '@/views/apps/referral-program/referral-programs/components/Condition.vue'
import MembersDiagram from '@/views/apps/referral-program/referral-programs/components/MembersDiagram.vue'
import CashbackDiagram from '@/views/apps/referral-program/referral-programs/components/CashbackDiagram.vue'
import ConditionModal from '@/views/apps/referral-program/referral-programs/components/ConditionModal.vue'
import ViewRulesetModal from '@/views/apps/referral-program/referral-programs/components/viewRuleset/ViewRulesetModal.vue'
import ViewFaqsetModal from '@/views/apps/referral-program/referral-programs/components/viewFaqset/ViewFaqsetModal.vue'
import ViewAdditionalConditionModal from '@/views/apps/referral-program/referral-programs/components/viewAdditionalConditions/ViewAdditionalConditionModal.vue'

import { getProgramParams } from '@/views/apps/referral-program/referral-programs/composables/useGetProgramParams.js'
import { programStatuses, getProgramStatusColors } from '@/views/apps/referral-program/referral-programs/composables/useProgramStatuses.js'
import { getReferralRoles } from '@/views/apps/referral-program/composables/useGetReferralRoles.js'
import ChangeStatusReferralProgramModal from '@/views/apps/referral-program/referral-programs/components/ChangeStatusReferralProgramModal.vue'

export default {
  props: {
    editPermission: {
      type: Boolean,
      default: false,
    },
    deletePermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeadSection,
    MainContainer,
    ContentBlock,
    FormTitle,
    FormSubTitle,
    DefaultLabelTab,
    Condition,
    MembersDiagram,
    CashbackDiagram,
    ConditionModal,
    ViewRulesetModal,
    ViewFaqsetModal,
    ViewAdditionalConditionModal,
    ChangeStatusReferralProgramModal,
  },
  data: () => ({
    permissionOfSection: 'referral_program',
    programId: null,
    program: {
      name: '',
      description: '',
      status: '',
      landing_link: '',
      conditions_link: '',
      started_at: null,
      expires_at: null,
      applications: [],
      languages: [],
      translations: [
      ],
      affiliate_settings: {
        role: {
          name: 'Аффилиат',
          system_name: 'affiliate',
        },
        faq_set: {
          name: '',
        },
        rule_set: {
          name: '',
        },
        bet_sum: 1100,
        invited_referrals: 3,
        avatar_category: {
          name: '',
        },
        cashback_percent: null,
        cashback_limit: null,
        conditions: [
          {
            type: 'join_program',
            translations: [],
          },
          {
            type: 'invite_referrals',
            translations: [],
          },
          {
            type: 'affiliate_bet_sum',
            translations: [],
          },
          {
            type: 'referral_bet_sum',
            translations: [],
          },
        ],
        additional_conditions: [],
      },
      referral_settings: {
        role: {
          name: 'Реферал',
          system_name: 'referral',
        },
        faq_set: {
          name: '',
        },
        rule_set: {
          name: '',
        },
        bet_sum: null,
        cashback_percent: null,
        cashback_limit: null,
        conditions: [
          {
            type: 'join_program',
            translations: [],
          },
          {
            type: 'affiliate_bet_sum',
            translations: [],
          },
          {
            type: 'referral_bet_sum',
            translations: [],
          },
        ],
        additional_conditions: [],
      },
    },
    activeRoleTab: undefined,
    availableRoles: [],
    getAvailableRolesLoading: false,
    loading: false,

    targetCondition: null,
    showConditionModal: false,

    selectedRuleset: null,
    showRulesetModal: false,

    selectedFaqset: null,
    showFaqsetModal: false,

    selectedConditions: [],
    showAddConditionModal: false,

    showChangeStatusRefProgramModal: false,
  }),
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    breadcrumbs() {
      return [
        { name: 'Referral programs', link: '/referral/programs' },
        { name: this.program?.name, link: null },
      ]
    },
    showStartStopButton() {
      if (this.program.status === programStatuses().disabled || this.program.status === programStatuses().completed) {
        return false
      }
      // const now = this.$moment()
      // const start = this.$moment(this.program.started_at)
      // if (!now.isSameOrBefore(start)) {
      //   return false
      // }
      return true
    },
    isActiveProgram() {
      return this.programStatusName === programStatuses().active
    },
    detailsButtonText() {
      if (this.programStatusName === programStatuses().inactive) {
        return 'Edit'
      }
      return 'Details'
    },
    innerProgramName() {
      return this.program?.name || ''
    },
    programName() {
      if (!this.program.languages.length) {
        return '-'
      }
      if (!this.programDefaultLanguage) {
        return '-'
      }
      return this.program?.translations.find(translation => translation.id === this.programDefaultLanguage.id)?.name || '-'
    },
    programDefaultLanguage() {
      return this.program.languages.find(lang => lang.is_default)
    },
    programStatusName() {
      return this.program?.status || ''
    },
    programStatusColors() {
      return getProgramStatusColors(this.program?.status)
    },
    programStartedAt() {
      if (!this.program?.started_at) {
        return null
      }
      const startedAt = this.$moment(this.program.started_at).tz(this.$moment.tz.guess())
      return {
        date: startedAt.format('DD.MM.YYYY'),
        time: startedAt.format('HH:mm'),
      }
    },
    programExpiresAt() {
      if (!this.program?.expires_at) {
        return null
      }
      const expiresAt = this.$moment(this.program.expires_at).tz(this.$moment.tz.guess())
      return {
        date: expiresAt.format('DD.MM.YYYY'),
        time: expiresAt.format('HH:mm'),
      }
    },
    programPercent() {
      if (!this.program?.started_at || !this.program?.expires_at) {
        return 0
      }
      const now = this.$moment()
      const start = this.$moment(this.program.started_at)
      const end = this.$moment(this.program.expires_at)

      if (now.isSameOrBefore(start)) {
        return 0
      }
      if (now.isSameOrAfter(end)) {
        return 100
      }

      const totalDuration = end.diff(start)
      const elapsed = now.diff(start)

      const percent = (elapsed / totalDuration) * 100

      return Math.min(100, Math.max(0, Math.round(percent)))
    },
    languagesUsed() {
      return this.program.languages.map(lang => {
        return {
          name: lang.name,
          value: lang.id,
          is_default: lang.is_default,
        }
      })
    },
    targetConditionValue() {
      if (!this.targetCondition) {
        return ''
      }
      return this.getConditionValue(this.targetCondition)
    },
  },
  methods: {
    toggleShowConditionModal(status = false, system_name = null, conditionIndex = null, condition = null) {
      this.targetCondition = { ...condition, system_name: system_name, index: conditionIndex }
      this.showConditionModal = status
    },
    toggleShowRulesetModal(status = false, ruleset = null) {
      this.selectedRuleset = ruleset
      this.showRulesetModal = status
    },
    toggleShowFaqsetModal(status = false, faqset = null) {
      this.selectedFaqset = faqset
      this.showFaqsetModal = status
    },
    toggleAddConditionModal(status = false, addConditions = null) {
      this.selectedConditions = addConditions
      this.showAddConditionModal = status
    },
    toggleChangeStatusRefProgramModal(status = false) {
      this.showChangeStatusRefProgramModal = status
    },
    updateRefProgramStatus(status) {
      this.program.status = status
    },
    getConditionValue(condition) {
      switch (condition?.type) {
        case 'join_program':
          return null
        case 'invite_referrals':
          return this.program['affiliate_settings'].invited_referrals
        case 'affiliate_bet_sum':
          return this.program['affiliate_settings'].bet_sum
        case 'referral_bet_sum':
          return this.program['referral_settings'].bet_sum
        default:
          return null
      }
    },
    activityLogProgram() {
      this.$router.push(`/referral/programs/${this.programId}/logs`)
    },
    detailsProgram() {
      this.$router.push(`/referral/programs/${this.programId}/edit`)
    },
    async getAvailableRoles() {
      try {
        this.getAvailableRolesLoading = true
        const roles = await getReferralRoles() || []
        this.availableRoles = roles.map(role => {
          return {
            id: role.id,
            name: role.name,
            system_name: role.system_name,
            error_validate: false,
          }
        })
        this.activeRoleTab = this.availableRoles[0].system_name
      } finally {
        this.getAvailableRolesLoading = false
      }
    },
    async getProgramParams() {
      const data = await getProgramParams(this.programId)
      if (data) {
        const { affiliate_settings, referral_settings, ...rest } = data

        this.program = {
          ...this.program,
          ...rest,
          ...(affiliate_settings && { affiliate_settings }),
          ...(referral_settings && { referral_settings }),
        }
        return
      }
      this.$router.push('/referral/programs')
    },
  },
  async mounted() {
    this.programId = this.$route.params.id

    try {
      this.loading = true
      const requests = [this.getAvailableRoles()]

      if (this.programId) {
        requests.push(this.getProgramParams())
      }

      await Promise.all(requests)
    } finally {
      this.$nextTick(() => {
        this.loading = false
      })
    }
  },
}
</script>

<style lang="scss">
.referral-program-view {
  .general-info {
    display: flex;
    flex-wrap: wrap;
    gap: 200px;
    @media (max-width: 768px) {
      gap: 20px;
    }
  }
  &__container {
    display: flex;
    flex-wrap: wrap;
    gap: 200px;
    @media (max-width: 768px) {
      gap: 20px;
    }
  }
  &__conditions {
    max-width: 500px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  &__cashback {
    max-width: 500px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1 1 auto;
  }
  &__block {
    padding: 10px 20px;
    border: 1px solid #e4e9f0;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    width: fit-content;
    transition: all .3s ease;
  }
  .block-hover:hover {
    border-color: #1890ff;
  }
  .statistic-button {
    &:hover, &:focus {
      border-color: #e9833a;
    }
  }

  .role-tabs {
    & > .ant-tabs-card > .ant-tabs-bar .ant-tabs-tab {
      margin-right: 0 !important;
    }
    & > .ant-tabs-card > .ant-tabs-content > .ant-tabs-tabpane {
      background: #fff;
      padding: 16px 8px 8px;
      border-right: 1px solid #e4e9f0;
      border-bottom: 1px solid #e4e9f0;
      border-left: 1px solid #e4e9f0;
      border-radius: 0 0 4px 4px;
    }
  }

  .ant-badge > sup {
    text-transform: capitalize;
  }
  .ant-progress .ant-progress-outer .ant-progress-inner .ant-progress-bg {
    height: 12px !important;
  }
}
</style>
